import { useMemo, useState } from "react"
import { toHtml } from "hast-util-to-html"
import dart from "highlight.js/lib/languages/dart"
import { common, createLowlight } from "lowlight"
import { IconChevronDownOutline18 as ChevronDown } from "nucleo-ui-outline-18"

import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"

import type { SnippetOptions } from "./install-snippet-data"
import { useZeroWorkspace } from "@/lib/zero"
import { MAIN_PORTAL_OPTION, SNIPPETS } from "./install-snippet-data"
import { SnippetCopyPopover } from "./snippet-copy-popover"

const lowlight = createLowlight(common)
lowlight.register("dart", dart)

const PICKER_CLASS =
	"relative inline-flex flex-none items-center gap-1 h-6 rounded-md border border-gray-200 bg-white px-2 text-xs text-secondary transition-colors duration-150 ease-out hover:text-primary dark:border-gray-700 dark:bg-gray-700/70"

export function InstallSnippets({
	widgetKey,
	options,
	onOptionsChange,
}: {
	widgetKey?: string
	options: SnippetOptions
	onOptionsChange: (options: SnippetOptions) => void
}) {
	const [activeId, setActiveId] = useState(SNIPPETS[0]!.id)
	const [portalSlug, setPortalSlug] = useState(MAIN_PORTAL_OPTION)
	const workspace = useZeroWorkspace()

	const portals = useMemo(() => {
		const list = workspace?.portalInstances ?? []
		return [...list].sort((a, b) => a.name.localeCompare(b.name))
	}, [workspace?.portalInstances])

	const activePortal = portals.find((portal) => portal.slug === portalSlug)

	const active = useMemo(() => {
		const snippet = SNIPPETS.find((s) => s.id === activeId) ?? SNIPPETS[0]!
		const code = snippet.build(widgetKey ?? "", {
			...options,
			portalInstance:
				portalSlug === MAIN_PORTAL_OPTION ? undefined : portalSlug,
		})
		return {
			...snippet,
			code,
			html: toHtml(lowlight.highlight(snippet.language, code)),
		}
	}, [activeId, widgetKey, portalSlug, options])

	return (
		<div className="relative overflow-hidden rounded-md bg-gray-800">
			<div className="absolute right-2 top-2 z-10 flex items-center gap-2 sm:right-3 sm:top-3">
				{portals.length > 0 && (
					<DropdownMenu
						contentProps={{ align: "end" }}
						trigger={
							<button type="button" className={PICKER_CLASS}>
								{activePortal?.name ?? "Main portal"}
								<ChevronDown className="h-3.5 w-3.5 flex-none" />
							</button>
						}
					>
						<DropdownMenuItem onClick={() => setPortalSlug(MAIN_PORTAL_OPTION)}>
							Main portal
						</DropdownMenuItem>
						{portals.map((portal) => (
							<DropdownMenuItem
								key={portal.id}
								onClick={() => setPortalSlug(portal.slug)}
							>
								{portal.name}
							</DropdownMenuItem>
						))}
					</DropdownMenu>
				)}
				<DropdownMenu
					contentProps={{ align: "end" }}
					trigger={
						<button type="button" className={PICKER_CLASS}>
							{active.label}
							<ChevronDown className="h-3.5 w-3.5 flex-none" />
						</button>
					}
				>
					{SNIPPETS.map((snippet) => (
						<DropdownMenuItem
							key={snippet.id}
							onClick={() => setActiveId(snippet.id)}
						>
							{snippet.label}
						</DropdownMenuItem>
					))}
				</DropdownMenu>
				<SnippetCopyPopover
					code={active.code}
					options={options}
					onChange={onOptionsChange}
					triggerClassName={PICKER_CLASS}
				/>
			</div>
			<pre className="max-h-64 w-full overflow-auto p-2 text-[11px] leading-relaxed text-gray-100 sm:p-4 sm:text-xs">
				<code
					className="block whitespace-pre-wrap break-words"
					dangerouslySetInnerHTML={{ __html: active.html }}
				/>
			</pre>
		</div>
	)
}
